import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

// Modules and the actions that can be granted. Permission strings are `module:action`.
const PERMS = [
  { m: 'dashboard', label: 'Dashboard', actions: ['read'] },
  { m: 'products', label: 'Products', actions: ['read', 'write'] },
  { m: 'rawmaterials', label: 'Raw Materials', actions: ['read', 'write'] },
  { m: 'suppliers', label: 'Suppliers', actions: ['read', 'write'] },
  { m: 'recipes', label: 'Recipes / BOM', actions: ['read', 'write'] },
  { m: 'production', label: 'Making', actions: ['read', 'write'] },
  { m: 'packing', label: 'Packing', actions: ['read', 'write'] },
  { m: 'procurement', label: 'Procurement (Warehouse)', actions: ['read', 'write'] },
  { m: 'transfers', label: 'Stock Requests / Transfers', actions: ['read', 'request', 'receive', 'fulfil', 'dispatch', 'write'] },
  { m: 'inventory', label: 'Inventory', actions: ['read', 'adjust'] },
  { m: 'thresholds', label: 'Reorder Levels (product min/max)', actions: ['read', 'write'] },
  { m: 'locations', label: 'Locations', actions: ['read', 'write'] },
  { m: 'customers', label: 'Customers', actions: ['read', 'write'] },
  { m: 'sales', label: 'Sales / Billing', actions: ['create', 'read', 'return'] },
  { m: 'reports', label: 'Reports', actions: ['read'] },
  { m: 'employees', label: 'Employees (HR)', actions: ['read', 'write'] },
  { m: 'payroll', label: 'Payroll', actions: ['read', 'write'] },
  { m: 'settings', label: 'Settings', actions: ['read', 'write'] },
  { m: 'users', label: 'Users', actions: ['read', 'write'] },
  { m: 'roles', label: 'Roles', actions: ['write'] },
];

// ── Role editor: name, description, and module-permission checkboxes ──
const RoleModal = ({ role, onClose, onSaved }) => {
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [perms, setPerms] = useState(() => new Set(role?.permissions || []));
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const full = perms.has('*');

  const has = (p) => {
    if (perms.has('*')) return true;
    const [mod] = p.split(':');
    return perms.has(`${mod}:*`) || perms.has(p);
  };
  const toggle = (p) => setPerms((prev) => { const s = new Set(prev); if (s.has(p)) s.delete(p); else s.add(p); return s; });
  const toggleFull = () => setPerms((prev) => (prev.has('*') ? new Set() : new Set(['*'])));
  const toggleModule = (row) => setPerms((prev) => {
    const s = new Set(prev);
    const keys = row.actions.map((a) => `${row.m}:${a}`);
    const allOn = keys.every((k) => s.has(k) || s.has(`${row.m}:*`) || s.has('*'));
    s.delete(`${row.m}:*`);
    keys.forEach((k) => s.delete(k));
    if (!allOn) keys.forEach((k) => s.add(k));
    return s;
  });

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const payload = { name: name.trim(), description, permissions: full ? ['*'] : [...perms] };
      if (!payload.name) throw new Error('Role name is required');
      const { data } = role ? await api.put(`/roles/${role.id}`, payload) : await api.post('/roles', payload);
      toast.success('Role saved'); onSaved(data.role); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={role ? `Edit role — ${role.name}` : 'Add role'} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save role'}</button></>}>
      <form onSubmit={save}>
        <div className="row">
          <div className="field"><label>Role name *</label><input value={name} onChange={(e) => setName(e.target.value)} required disabled={!!role} /></div>
          <div className="field" style={{ flex: 2 }}><label>Description</label><input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        <label className="flex gap" style={{ alignItems: 'center', margin: '.5rem 0' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={full} onChange={toggleFull} /> <strong>Full access</strong> <span className="muted small">(everything — for admins)</span>
        </label>
        {!full && (
          <div className="table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
            <table className="grid">
              <thead><tr><th style={{ width: 160 }}>Module</th><th>Access</th></tr></thead>
              <tbody>
                {PERMS.map((row) => (
                  <tr key={row.m}>
                    <td><button type="button" className="link-strong" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => toggleModule(row)}>{row.label}</button></td>
                    <td>
                      <div className="flex gap" style={{ flexWrap: 'wrap' }}>
                        {row.actions.map((a) => {
                          const p = `${row.m}:${a}`;
                          return <label key={a} className="chk"><input type="checkbox" checked={has(p)} onChange={() => toggle(p)} /> {a}</label>;
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">Tip: click a module name to toggle all its actions. Assign one or more roles to a user — access is the union of their roles.</p>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

// ── User editor: multiple roles + home location ──
const UserModal = ({ user, roles, locations, onClose, onSaved }) => {
  const [form, setForm] = useState(user
    ? { name: user.name, email: user.email, locationId: user.locationId || '', isActive: user.isActive, password: '' }
    : { name: '', email: '', locationId: '', isActive: true, password: '' });
  const [roleIds, setRoleIds] = useState(user?.roleIds || (roles[0] ? [roles[0].id] : []));
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleRole = (id) => setRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const payload = { ...form, roleIds, locationId: Number(form.locationId) || null };
      if (user && !payload.password) delete payload.password;
      const { data } = user ? await api.put(`/users/${user.id}`, payload) : await api.post('/users', payload);
      toast.success('Saved'); onSaved(data.user); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={user ? 'Edit User' : 'Add User'} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>Save</button></>}>
      <form onSubmit={save}>
        <div className="field"><label>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
        <div className="field"><label>Roles</label>
          <div className="flex gap" style={{ flexWrap: 'wrap' }}>
            {roles.map((r) => <label key={r.id} className="chk"><input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} /> {r.name}</label>)}
          </div>
          <span className="muted small">A user can hold several roles; permissions are combined.</span>
        </div>
        <div className="field"><label>Home store / branch</label>
          <select value={form.locationId} onChange={(e) => set('locationId', e.target.value)}>
            <option value="">— none (sees all) —</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <span className="muted small">A store manager sees only their own requests and transfers into this location.</span>
        </div>
        <div className="field"><label>Email *</label><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required disabled={!!user} /></div>
        <div className="field"><label>{user ? 'New password (leave blank to keep)' : 'Password *'}</label><input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} /></div>
        <label className="flex gap" style={{ alignItems: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Active</label>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

const Users = () => {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [editingRole, setEditingRole] = useState(undefined);

  const load = () => { setLoading(true); api.get('/users').then(({ data }) => setUsers(data.users || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  const loadRoles = () => api.get('/roles').then(({ data }) => setRoles(data.roles || [])).catch(() => {});
  useEffect(() => { load(); loadRoles(); api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {}); }, []);

  const dt = useDataTable(users, {
    searchPlaceholder: 'Search name, email…',
    searchKeys: ['name', 'email'],
    filters: [
      { key: 'role', label: 'Role', value: (u) => u.role },
      { key: 'status', label: 'Status', value: (u) => (u.isActive ? 'Active' : 'Inactive') },
      { key: 'location', label: 'Store / branch', value: (u) => u.location },
    ],
  });

  return (
    <div>
      <TableToolbar dt={dt} right={<>
        {can('users:write') && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add User</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Name</th><th>Email</th><th>Roles</th><th>Store / branch</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {dt.rows.map((u) => (
                <tr key={u.id}>
                  <td><strong>{u.name}</strong></td><td>{u.email}</td>
                  <td>{(u.roles && u.roles.length ? u.roles : (u.role ? [{ id: 'x', name: u.role }] : [])).map((r) => <span key={r.id} className="badge badge-blue" style={{ marginRight: 4 }}>{r.name}</span>) || '—'}</td>
                  <td className="muted">{u.location || '—'}</td>
                  <td>{u.isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">Inactive</span>}</td>
                  <td>{can('users:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditing(u)}>Edit</button>}</td>
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={6} className="empty">{dt.isFiltered ? 'No users match your search.' : 'No users yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}

      <div className="section-head mt">
        <h3 style={{ fontSize: '.95rem' }}>Roles</h3>
        {can('roles:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditingRole(null)}>+ Add Role</button>}
      </div>
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Role</th><th>Description</th><th>Permissions</th><th></th></tr></thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong></td>
                <td>{r.description}</td>
                <td className="muted mono" style={{ whiteSpace: 'normal' }}>{(r.permissions || []).includes('*') ? 'Full access' : (r.permissions || []).join(', ')}</td>
                <td>{can('roles:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditingRole(r)}>Edit</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== undefined && <UserModal user={editing} roles={roles} locations={locations} onClose={() => setEditing(undefined)} onSaved={load} />}
      {editingRole !== undefined && <RoleModal role={editingRole} onClose={() => setEditingRole(undefined)} onSaved={loadRoles} />}
    </div>
  );
};

export default Users;
